import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Heart, Users, BookOpen, Calendar, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VolunteerPage = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    city: '',
    availability: '',
    skills: '',
    motivation: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, availability: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/volunteer`, formData);
      toast.success(t.volunteer.successMsg);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        city: '',
        availability: '',
        skills: '',
        motivation: '',
      });
    } catch (error) {
      console.error('Error submitting volunteer form:', error);
      toast.error(t.common.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const impactItems = [
    { id: 'support', icon: Users, text: t.volunteer.impact1 },
    { id: 'education', icon: BookOpen, text: t.volunteer.impact2 },
    { id: 'events', icon: Calendar, text: t.volunteer.impact3 },
    { id: 'care', icon: Heart, text: t.volunteer.impact4 },
  ];

  const availabilityOptions = language === 'fr' 
    ? ['Quelques heures par semaine', 'Quelques heures par mois', 'Événements ponctuels', 'Flexible']
    : ['A few hours per week', 'A few hours per month', 'Occasional events', 'Flexible'];

  return (
    <div data-testid="volunteer-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="section-label">{t.cta.volunteerBtn}</span>
            <h1 
              data-testid="volunteer-title"
              className="font-serif text-4xl sm:text-5xl font-bold text-[#1A252C] mt-4 mb-6"
            >
              {t.volunteer.pageTitle}
            </h1>
            <p className="text-lg text-[#546A76] leading-relaxed">
              {t.volunteer.whyText}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Why Volunteer */}
            <div>
              <h2 className="font-serif text-3xl font-bold text-[#1A252C] mb-8">
                {t.volunteer.whyTitle}
              </h2>
              <p className="text-[#546A76] leading-relaxed mb-10">
                {t.volunteer.whyText}
              </p>

              <h3 className="font-serif text-xl font-semibold text-[#1A252C] mb-6">
                {t.volunteer.impactTitle}
              </h3>
              <div className="space-y-4">
                {impactItems.map((item) => (
                  <div 
                    key={item.id}
                    data-testid={`impact-item-${item.id}`}
                    className="flex items-start gap-4 p-4 rounded-2xl bg-[#FAFAFA]"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#4A708B]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#4A708B]" />
                    </div>
                    <p className="text-[#1A252C] pt-2">{item.text}</p>
                  </div>
                ))}
              </div>

              {/* Image */}
              <div className="mt-12 rounded-3xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1764662366905-53eaff1d411a?w=800"
                  alt="Bénévoles soutenant des femmes et des enfants victimes de violence au Québec"
                  loading="lazy"
                  className="w-full h-[300px] object-cover"
                />
              </div>
            </div>

            {/* Right - Form */}
            <div>
              <div className="bg-[#FAFAFA] rounded-3xl p-8 sm:p-10">
                <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-8">
                  {t.volunteer.formTitle}
                </h2>

                <form onSubmit={handleSubmit} data-testid="volunteer-form" className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">{t.volunteer.firstName} *</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        data-testid="volunteer-firstname"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">{t.volunteer.lastName} *</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        data-testid="volunteer-lastname"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t.volunteer.email} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="volunteer-email"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t.volunteer.phone} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="volunteer-phone"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">{t.volunteer.city} *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        data-testid="volunteer-city"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="availability">{t.volunteer.availability} *</Label>
                    <Select onValueChange={handleSelectChange} value={formData.availability}>
                      <SelectTrigger 
                        data-testid="volunteer-availability"
                        className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                      >
                        <SelectValue placeholder={t.volunteer.availability} />
                      </SelectTrigger>
                      <SelectContent>
                        {availabilityOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="skills">{t.volunteer.skills} *</Label>
                    <Textarea
                      id="skills"
                      name="skills"
                      value={formData.skills}
                      onChange={handleChange}
                      required
                      rows={3}
                      data-testid="volunteer-skills"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="motivation">{t.volunteer.motivation} *</Label>
                    <Textarea
                      id="motivation"
                      name="motivation"
                      value={formData.motivation}
                      onChange={handleChange}
                      required
                      rows={4}
                      data-testid="volunteer-motivation"
                      className="rounded-xl bg-white border-[#E2E8F0] focus:border-[#4A708B]"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="volunteer-submit"
                    className="w-full primary-btn rounded-full py-6 text-lg font-medium"
                  >
                    {isSubmitting ? t.common.loading : t.volunteer.submit}
                    {!isSubmitting && <CheckCircle className="ml-2 w-5 h-5" />}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VolunteerPage;
